import { animate, state, style, transition, trigger } from '@angular/animations';
import {
    AfterContentChecked,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents, TaskFilterTypes, TaskStatusTypes } from '@app/enums';
import { getElementOffsetTop } from '@app/functions';
import { Employee } from '@app/models/employee/employee.model';
import { Task } from '@app/models/tasks/task.model';
import { DeprecatedTaskChecklistComponent } from '@app/modules/employees/components/task-checklist/deprecated/deprecated-task-checklist.component';
import { AnalyticService, AuthService, NotifyService, TasksService } from '@app/services';
import { combineLatest, Subscription } from 'rxjs';
import { DashboardTasksService } from '../../services/dashboard-tasks.service';

const RESULTS_PER_PAGE = 25;
@Component({
    selector: 'app-task-viewer',
    templateUrl: './task-viewer.template.html',
    styleUrls: ['./task-viewer.style.scss'],
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: 0 })),
            transition(':enter', [animate('.35s 700ms', style({ opacity: 1 }))]),
        ]),
    ],
})
export class TaskViewerComponent implements OnInit, AfterContentChecked, OnDestroy {
    @ViewChild('container', { static: true }) container: ElementRef;
    @ViewChild('editTaskModal') editTaskModal: DeprecatedModalFormComponent;
    @ViewChild('taskChecklist') taskChecklist: DeprecatedTaskChecklistComponent;

    @Input() legacy = false;
    @Input() showTaskId: number = null;

    @Output() updateIncompleteTasksCount = new EventEmitter<boolean>();
    isLoading = true;
    employee: Employee;

    tasks: Task[] = [];

    currentTask: Task;
    currentTaskFilter: TaskFilterTypes = TaskFilterTypes.myself;

    taskFilter = TaskFilterTypes.myself;
    taskType = TaskStatusTypes.incomplete;

    currentPage = 1;
    viewTaskDetailsAnalyticEvent = AnalyticEvents.DashboardTaskNotificationViewed;

    protected tasksLoadSubscription: Subscription;

    constructor(
        protected auth: AuthService,
        protected notify: NotifyService,
        protected dashboardTasksService: DashboardTasksService,
        protected analyticService: AnalyticService,
        private tasksService: TasksService
    ) {
        this.employee = this.auth.user;
        combineLatest([this.dashboardTasksService.onLoading(), this.tasksService.onLoading()])
            .pipe(takeUntilDestroyed())
            .subscribe(
                ([isDashboardTaskLoading, isTaskLoading]) => (this.isLoading = isDashboardTaskLoading || isTaskLoading)
            );
        dashboardTasksService.loadAllTasks();
    }

    ngOnInit(): void {
        this.tasksLoadSubscription = this.dashboardTasksService.onLoading().subscribe((isLoading) => {
            if (!isLoading) {
                this.setTasks();
            }
        });
    }

    ngAfterContentChecked(): void {
        this.resizeTaskContainer();
    }

    ngOnDestroy(): void {
        this.tasks = null;
        this.tasksLoadSubscription.unsubscribe();
    }

    @HostListener('window:resize')
    resizeTaskContainer(): void {
        if (!this.legacy) {
            return;
        }

        if (!this.container) {
            return;
        }

        const offsetTop = getElementOffsetTop(this.container.nativeElement);
        const windowHeight = window.innerHeight;
        const borderSize = 2;

        this.container.nativeElement.style.height = windowHeight - offsetTop - borderSize + 'px';
    }

    onChangeTaskFilter(taskFilter: TaskFilterTypes): void {
        this.taskFilter = taskFilter;
        this.setTasks();
    }

    onChangeTab(tab: TaskStatusTypes): void {
        this.taskType = tab;
        this.setTasks();
    }

    updateTasks(): void {
        this.currentPage = 1;

        this.dashboardTasksService.reloadTasks(this.currentPage).then(() => {
            this.setTasks();
        });
    }

    showTaskModal(task: Task): void {
        this.currentTask = task;
        this.editTaskModal.show();
    }

    onCancel(): void {}

    onRemind(): void {
        this.analyticService.trackEvent(AnalyticEvents.DashboardTaskNotificationReminded);
    }

    onDelete(): void {
        this.analyticService.trackEvent(AnalyticEvents.DashboardTaskNotificationDeleted);
    }

    onViewed(): void {
        this.analyticService.trackEvent(AnalyticEvents.DashboardTaskNotificationViewed);
    }

    saveTask(currentTask: Task): void {
        currentTask
            .save()
            .then(() => {
                this.notify.success('Task has been edited.');
                this.analyticService.trackEvent(AnalyticEvents.DashboardTaskNotificationEdited);
            })
            .catch((err) => {
                this.notify.error(err.message);
            });
    }

    onScroll(): void {
        const tasks = this.taskChecklist.container.nativeElement.querySelectorAll('.task');

        if (tasks.length) {
            const element = this.container.nativeElement;
            const atBottom = element.scrollTop + element.offsetHeight >= element.scrollHeight;

            if (atBottom && this.currentPage * RESULTS_PER_PAGE <= this.getTotalTasksOfCurrentFilter()) {
                this.currentPage++;
                this.dashboardTasksService.loadMoreTasks(this.currentPage, this.taskType, this.taskFilter);
                this.setTasks();
            }
        }
    }

    getEmptyStateMessage(): string {
        if (this.tasks.length) {
            return '';
        }

        if (this.taskFilter === TaskFilterTypes.company) {
            if (this.taskType === TaskStatusTypes.complete) {
                return 'dashboard.noCompletedTasks';
            }

            if (this.taskType === TaskStatusTypes.incomplete) {
                return 'dashboard.noUnassignedTasks';
            }
        }

        if (this.taskFilter === TaskFilterTypes.myself) {
            if (this.taskType === TaskStatusTypes.complete) {
                return 'dashboard.noCompletedTasksForSelf';
            }

            if (this.taskType === TaskStatusTypes.incomplete) {
                return 'dashboard.noAssignedTasksForSelf';
            }
        }
    }

    private setTasks(): void {
        if (this.taskFilter === TaskFilterTypes.myself) {
            this.tasks = this.dashboardTasksService.getUserTasks(this.taskType);
        } else {
            this.tasks = this.dashboardTasksService.getCompanyTasks(this.taskType);
        }
    }

    private getTotalTasksOfCurrentFilter(): number {
        if (this.taskFilter === TaskFilterTypes.myself) {
            if (this.taskType === TaskStatusTypes.complete) {
                return this.dashboardTasksService.totalCompleteUserTasks;
            }
            return this.tasksService.totalIncompleteUserTasks;
        }

        if (this.taskFilter === TaskFilterTypes.company) {
            if (this.taskType === TaskStatusTypes.complete) {
                return this.dashboardTasksService.totalCompleteCompanyTasks;
            }
            return this.dashboardTasksService.totalIncompleteCompanyTasks;
        }
    }
}
